import { withScriptFallback } from '@messenger/lotus/fonts'
import { regEx } from '@/utils/global/regEx'
import { getCursorPositionStyles } from '@/utils/tinyMCE/getCursorPositionStyles'
import type { GetThemeNormalized as SelectedTheme } from '@messenger/types'

type GetThemeStylesProps = {
  selectedTheme: SelectedTheme | null
  editor?: Editor
  firstInsertion?: boolean
}

// Validate if color string is valid
const isValidColor = (color: string): boolean => {
  return regEx.validColor.test(color)
}

/**
 * Gets styles for content insertion into TinyMCE
 *
 * Priority order:
 * 1. If NOT first insertion and editor available → use cursor position styles (template updates)
 * 2. If editor available and cursor has user-applied styles → use cursor styles (inserting into styled text)
 * 3. Otherwise → use theme default styles (new empty text box)
 *
 * This ensures:
 * - New text boxes use theme defaults
 * - Inserting case details into styled text preserves that styling
 * - Template updates preserve existing formatting
 */
export const getThemeStylesForTemplate = (
  props: GetThemeStylesProps,
): string => {
  const { selectedTheme, editor, firstInsertion = true } = props

  try {
    // For templates: if updating existing template (not first insertion), use cursor styles
    if (!firstInsertion && editor) {
      const styles = getCursorPositionStyles(editor)
      return styles
    }

    // For case details and templates: if cursor has user-applied styles, use those instead
    if (editor && firstInsertion) {
      const cursorStyles = getCursorPositionStyles(editor)
      const hasUserAppliedStyles = cursorStyles.length > 0

      if (hasUserAppliedStyles) {
        return cursorStyles
      }
    }

    // Default: use theme styles (new text box or no user styles detected)
    // If no theme exists, return empty string
    if (!selectedTheme) {
      return ''
    }

    const font = selectedTheme.font?.trim() ?? ''
    const fontColor = selectedTheme.font_color?.trim() ?? ''

    // Append Noto Script Fallback so theme-template fonts get the same
    // multi-script coverage as picker / default selections. See #2517.
    const themeFontFamily = font
      ? `font-family: ${withScriptFallback(font)};`
      : ''
    const themeFontColor =
      fontColor && isValidColor(fontColor) ? `color: ${fontColor};` : ''
    const styles = `${themeFontFamily} ${themeFontColor}`.trim()

    return styles
  } catch {
    return ''
  }
}

export default getThemeStylesForTemplate
